import React, { useMemo } from "react";
import { TooltipHost } from "@fluentui/react";
import { ILayout } from "../CustomTemplate";
import { InputType } from "../Validations";
import { PageItem } from "../../../share/CustomPaginate";

/**
 * FormStepRail
 *
 * Named, clickable steps shown instead of the numeric pager on the public
 * form-link page (docSource === 'link'). The in-app editor keeps the pager.
 *
 * Labels are derived from the first SectionHeader on each page — content the
 * generator already emits — so nothing new has to be authored in templates.
 * Pages with no SectionHeader fall back to "Page N".
 *
 * IMPORTANT: this control does NOT navigate on its own. It calls onSelectPage,
 * which is wired to the same validated navigation FormPaginate uses, so
 * required-field validation and conditionally disabled pages behave identically
 * whichever control the user clicks.
 */

export interface IFormStepRailProps {
    pages: number;
    /** 1-based, matching CustomForm's currentPage. */
    currentPage: number;
    layouts: ILayout[];
    /** Same PageItem list FormPaginate holds; carries per-page disabled state. */
    pageItems?: PageItem[];
    /** Receives a 0-based page index, matching FormPaginate's nextSelectedPage. */
    onSelectPage: (pageIndex: number) => void;
    /** 1-based page numbers that failed required-field validation on submit; their
     *  step markers pulse red (except the page the user is currently on). */
    errorPages?: number[];
}

interface IStep {
    /** 1-based page number. */
    page: number;
    label: string;
    /** Full, untruncated label — shown as the hover tooltip. */
    full: string;
    count: number;
}

/** Controls that count as an answerable field for the per-step count. */
const ANSWERABLE: InputType[] = [
    InputType.Textbox,
    InputType.TextArea,
    InputType.DropDownList,
    InputType.CheckBox,
    InputType.CheckBoxList,
    InputType.RadioButtonList,
    InputType.DatePicker,
    InputType.Number,
    InputType.Currency,
    InputType.RichText,
    InputType.GridList,
];

export const buildSteps = (pages: number, layouts: ILayout[]): IStep[] => {
    const steps: IStep[] = [];

    for (let page = 1; page <= pages; page++) {
        const onPage = (layouts || []).filter((l) => +l.Page === page);

        // Section headers on this page, in layout order.
        const headers = onPage.filter(
            (l) => l.Type === InputType.SectionHeader && !!l.Label
        );

        // Header ARRAY order is not visual order, and page 1 often opens with a
        // non-numbered document title. Sort top-to-bottom, then prefer the first
        // NUMBERED section header ("1. …") — that's the section that names the page
        // (e.g. "1. Candidate Details"). Fall back to the topmost header (skipping a
        // lone page-1 title) when nothing on the page is numbered.
        const sorted = [...headers].sort(
            (a, b) => (a.Layout.y - b.Layout.y) || (a.Layout.x - b.Layout.x)
        );
        const ordinalRe = /^\s*\d+\s*[.)]/;
        const cleanLabel = (l: ILayout) =>
            (l.Label || "").replace(/<[^>]*>/g, " ").trim();
        let header = sorted.find((h) => ordinalRe.test(cleanLabel(h)));
        if (!header) {
            header = sorted.length > 1 && page === 1 ? sorted[1] : sorted[0];
        }

        let label = (header?.Label || "").trim();

        // Strip markup if the header carries rich text, then collapse space.
        label = label.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

        // Templates commonly prefix headers with "1. " or "2) " — drop a leading
        // ordinal so the rail doesn't read "1. 1. Company Details".
        label = label.replace(/^\d+\s*[.)]\s*/, "").trim();

        if (!label) label = `Page ${page}`;
        // Keep the full label for the tooltip before truncating the visible one.
        const full = label;
        if (label.length > 34) label = label.slice(0, 33).trimEnd() + "\u2026";

        const count = onPage.filter(
            (l) => ANSWERABLE.indexOf(l.Type) !== -1
        ).length;

        steps.push({ page, label, full, count });
    }

    return steps;
};

const FormStepRail: React.FC<IFormStepRailProps> = ({
    pages,
    currentPage,
    layouts,
    pageItems,
    onSelectPage,
    errorPages,
}) => {
    const steps = useMemo(() => buildSteps(pages, layouts), [pages, layouts]);

    // Hide entirely on single-page forms; there is nothing to navigate.
    if (!pages || pages <= 1) return null;

    const isDisabled = (page: number): boolean => {
        if (!pageItems || pageItems.length === 0) return false;
        const item = pageItems.find((p) => p.name === `${page}`);
        return !!(item && item.disabled);
    };

    const enabled = steps.filter((s) => !isDisabled(s.page));
    // Position of the current step among the enabled ones (0-based). Steps
    // before it get the "done" tick; it and later steps do not.
    const position = enabled.findIndex((s) => s.page === currentPage);
    const total = enabled.length;
    // The Option 8b mock counts the current section toward progress: on section
    // 1 of 5 it reads "1 of 5" with the bar ~20% filled. So `reached` is the
    // 1-based index of the current step, and the bar tracks reached / total.
    const reached = position < 0 ? 0 : position + 1;
    const pct = total > 0 ? Math.round((reached / total) * 100) : 0;

    return (
        <section className="fl-rail" aria-label="Form sections">
            <div className="fl-rail-head">
                <span className="fl-rail-count">
                    Page {reached} of {total}
                </span>
                <div
                    className="fl-rail-bar"
                    role="progressbar"
                    aria-valuenow={reached}
                    aria-valuemin={0}
                    aria-valuemax={total}
                >
                    <i style={{ width: `${pct}%` }} />
                </div>
            </div>

            <ol className="fl-rail-list">
                {steps.map((s) => {
                    if (isDisabled(s.page)) return null;

                    const isCurrent = s.page === currentPage;
                    const isDone =
                        position >= 0 &&
                        enabled.findIndex((e) => e.page === s.page) < position;
                    // Pulse a page that has a missed required field, but only when the
                    // user isn't currently on it (they'd see that page's inline errors).
                    const isError =
                        !isCurrent && !!errorPages && errorPages.indexOf(s.page) !== -1;

                    const cls = [
                        "fl-step",
                        isCurrent ? "is-current" : "",
                        isDone ? "is-done" : "",
                        isError ? "is-error" : "",
                    ]
                        .filter(Boolean)
                        .join(" ");

                    return (
                        <li key={s.page} className={cls}>
                            <button
                                type="button"
                                className="fl-step-btn"
                                aria-current={isCurrent ? "step" : undefined}
                                // FormPaginate works in 0-based indexes.
                                onClick={() => onSelectPage(s.page - 1)}
                            >
                                <span className="fl-step-marker" aria-hidden="true">
                                    {s.page}
                                </span>
                                {/* Tooltip shows the full section name on every hover — the
                                    visible label is clamped to 2 lines / 34 chars. TooltipHost
                                    renders in a portal so it isn't clipped by the rail's
                                    horizontal scroll container. */}
                                <TooltipHost
                                    content={s.full}
                                    styles={{ root: { display: "block", maxWidth: "15ch" } }}
                                >
                                    <span className="fl-step-label">{s.label}</span>
                                </TooltipHost>
                            </button>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
};

export default FormStepRail;
